import { useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Edit,
    Users,
    Copy,
    MoreVertical,
    ClipboardCheck,
    Trophy,
    Archive,
    Trash2,
    BadgeCheck,
    RefreshCw,
    Download,
    type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useToast } from '@/components/ui/Toast';

interface AdminMenuProps {
    raceId: string;
    isOpen: boolean;
    onToggle: () => void;
    onClose: () => void;
    isArchived: boolean;
    isEnded: boolean;
    isRaceOpen: boolean;
    inviteCode?: string | null;
    isOfficial?: boolean;
    isPrivate?: boolean;
    isAdmin?: boolean;
    isByUtmb?: boolean;
    isAutoSyncAvailable?: boolean;
    onArchive?: () => void;
    onDelete?: () => void;
    onToggleOfficial?: () => void;
    onSyncRunners?: () => void;
    onSyncResults?: () => void;
}

function MenuItem({ icon: Icon, label, onClick, className }: {
    icon: LucideIcon;
    label: string;
    onClick?: () => void;
    className?: string;
}) {
    return (
        <button
            onClick={onClick}
            className={cn(
                "w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors text-left",
                className
            )}
        >
            <Icon size={18} className="text-slate-400" />
            {label}
        </button>
    );
}

export function AdminMenu({ raceId, isOpen, onToggle, onClose, isArchived, isEnded, isRaceOpen, inviteCode, isOfficial, isPrivate, isAdmin, isByUtmb, isAutoSyncAvailable, onArchive, onDelete, onToggleOfficial, onSyncRunners, onSyncResults }: AdminMenuProps) {
    const navigate = useNavigate();
    const { t } = useTranslation('races');
    const { toast } = useToast();
    const menuRef = useRef<HTMLDivElement>(null);
    useClickOutside(menuRef, useCallback(() => onClose(), [onClose]), isOpen);

    const copyInviteLink = () => {
        if (inviteCode) {
            navigator.clipboard.writeText(`${window.location.origin}/join/${inviteCode}`);
            onClose();
            toast(t('details.linkCopied'), 'success');
        }
    };

    return (
        <div className="relative" ref={menuRef}>
            <button onClick={onToggle} className="p-2 hover:bg-slate-100 rounded-full">
                <MoreVertical size={20} />
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in zoom-in-95 origin-top-right z-50">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t('details.admin')}</p>
                    </div>
                    <div className="flex flex-col py-1">
                        {!isArchived && (
                            <MenuItem
                                icon={ClipboardCheck}
                                label={t('results.enterResults')}
                                onClick={() => { onClose(); navigate(`/races/${raceId}/results`); }}
                            />
                        )}
                        {isEnded && (
                            <Link to={`/races/${raceId}/leaderboard`} onClick={onClose}>
                                <MenuItem icon={Trophy} label={t('results.viewLeaderboard')} />
                            </Link>
                        )}
                        {isRaceOpen && (
                            <Link to={`/races/${raceId}/add-runner`} onClick={onClose}>
                                <MenuItem icon={Users} label={t('details.manageRunners')} />
                            </Link>
                        )}
                        {isRaceOpen && (
                            <MenuItem
                                icon={Edit}
                                label={t('details.editRace')}
                                onClick={() => { onClose(); navigate(`/races/${raceId}/edit`); }}
                            />
                        )}
                        <MenuItem icon={Copy} label={t('details.copyLink')} onClick={copyInviteLink} />
                        {isAdmin && isRaceOpen && isOfficial && isByUtmb && onSyncRunners && (
                            <MenuItem
                                icon={RefreshCw}
                                label={t('details.syncRunners')}
                                onClick={() => { onClose(); onSyncRunners(); }}
                                className="text-blue-600"
                            />
                        )}
                        {isAdmin && isAutoSyncAvailable && !isArchived && onSyncResults && (
                            <MenuItem
                                icon={Download}
                                label={t('details.syncResults')}
                                onClick={() => { onClose(); onSyncResults(); }}
                                className="text-emerald-600"
                            />
                        )}
                        {isAdmin && !isPrivate && onToggleOfficial && (
                            <MenuItem
                                icon={BadgeCheck}
                                label={isOfficial ? t('details.removeOfficial') : t('details.makeOfficial')}
                                onClick={() => { onClose(); onToggleOfficial(); }}
                                className={isOfficial ? 'text-amber-600' : undefined}
                            />
                        )}
                        {isEnded && !isArchived && onArchive && (
                            <MenuItem
                                icon={Archive}
                                label={t('details.archiveRace')}
                                onClick={() => { onClose(); onArchive(); }}
                                className="text-slate-500"
                            />
                        )}
                        {onDelete && (
                            <>
                                <div className="border-t border-slate-100 my-1" />
                                <MenuItem
                                    icon={Trash2}
                                    label={t('details.deleteRace')}
                                    onClick={() => { onClose(); onDelete(); }}
                                    className="text-red-600"
                                />
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
